import { useQuery } from "@apollo/client/react";
import {
  Box,
  Card,
  Center,
  Group,
  Loader,
  Progress,
  rem,
  SimpleGrid,
  Text,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import {
  IconCalendar,
  IconCalendarEvent,
  IconDownload,
  IconMap,
  IconTicket,
} from "@tabler/icons-react";
import domtoimage from "dom-to-image-more";
import { ResultOf, graphql } from "gql.tada";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { useUserGroups } from "../../../../business/groups.hook";
import { usePaginatedLoadAll } from "../../../../business/paginated-query.hook";
import { CDN_BASE_URL } from "../../../../utils/cdn";
import { DetailPageHeader } from "../../components/PageHeader";
import { StatCard } from "../../components/StatCard";
import { StatusBadge } from "../../components/StatusBadge";
import { DetailPageSkeleton } from "../../components/SkeletonLoader";
import { AbbrevitatedNumber } from "../../components/AbbrevitatedNumber";
import {
  useOrganizationLimits,
  formatLimitValue,
} from "../../../../business/limits.hook";

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    borderRadius: theme.radius.lg,
    overflow: "hidden",
    transition: "border-color 200ms ease",
    "&:hover": {
      border: `1px solid ${theme.colors.dark[3]}`,
    },
  },
  cardHeader: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.dark[7],
    borderBottom: `1px solid ${theme.colors.dark[5]}`,
  },
  cardHeaderIcon: {
    width: rem(40),
    height: rem(40),
    borderRadius: theme.radius.md,
    backgroundColor: `${theme.colors.cyan[8]}20`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colors.cyan[4],
  },
  cardTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
    color: theme.colors.dark[0],
  },
  cardBody: {
    padding: theme.spacing.md,
  },
  ticketCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.sm,
    marginBottom: theme.spacing.xs,
    "&:last-child": {
      marginBottom: 0,
    },
  },
  ticketName: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    color: theme.colors.dark[0],
  },
  ticketCount: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color: theme.colors.cyan[4],
  },
  seatsCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    borderRadius: theme.radius.lg,
    overflow: "hidden",
    transition: "border-color 200ms ease",
    "&:hover": {
      border: `1px solid ${theme.colors.dark[3]}`,
    },
  },
}));

const EVENT_DETAILS = graphql(`
  query eventDetails($id: ID!) {
    event(id: $id) {
      title
      startAt
      endAt
      startRegistrationsAt
      endRegistrationsAt
      visibleAt
      venue {
        name
        configuration {
          imageUrl
        }
      }
    }

    eventTicketConfigurations(eventId: $id) {
      name
      currentCount
      maxCount
    }
  }
`);

const EVENT_VENUE_SEATS_AVAILABLE = graphql(`
  query eventSeats($eventId: ID!, $page: PageInfo!) {
    eventSeats(eventId: $eventId, page: $page) {
      nodes {
        id
        name
        seatTypeId
        status
        reservedByReservationId
        reservedByGroupMemberId
        configuration {
          position {
            x
            y
          }
          size
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const EVENT_RESERVATIONS = graphql(`
  query eventReservation($reservationId: ID!) {
    eventReservation(reservationId: $reservationId) {
      name
    }
  }
`);

const EVENT_RESERVATIONS_CONFIRMED = graphql(`
  query eventReservations($eventId: ID!, $page: PageInfo!) {
    eventReservations(
      eventId: $eventId
      page: $page
      query: { filterByRegistrationStatus: CONFIRMED }
    ) {
      nodes {
        id
        name
        groupId
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const SeatModal = ({ reservationId }: { reservationId: string }) => {
  const { loading: reservationLoading, data: reservation } = useQuery<any>(
    EVENT_RESERVATIONS,
    {
      variables: { reservationId },
    },
  );

  if (reservationLoading) {
    return <Loader />;
  }

  return <Text>Reserved by: {reservation?.eventReservation?.name}</Text>;
};

const SeatsMap = ({
  eventId,
  imageUrl,
}: {
  eventId: string;
  imageUrl: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [, setPreloadedImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    const imageSrc = `${CDN_BASE_URL}/${imageUrl}`;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setPreloadedImage(img);
    };
    img.src = imageSrc;
  }, [imageUrl]);

  const { loading: seatsLoading, results: seats } = usePaginatedLoadAll<
    any,
    any,
    ResultOf<typeof EVENT_VENUE_SEATS_AVAILABLE>["eventSeats"]["nodes"][0]
  >(EVENT_VENUE_SEATS_AVAILABLE, {
    variables: {
      eventId,
    },
  });

  const { loading: reservationsLoading, results: reservations } =
    usePaginatedLoadAll<
      any,
      any,
      ResultOf<
        typeof EVENT_RESERVATIONS_CONFIRMED
      >["eventReservations"]["nodes"][0]
    >(EVENT_RESERVATIONS_CONFIRMED, {
      variables: { eventId },
    });

  const groupIds = useMemo(() => {
    return reservations?.map((reservation) => reservation.groupId) ?? [];
  }, [reservations]);
  const { results: groups, loading: groupsLoading } = useUserGroups({
    groupIds,
    skip: !groupIds.length,
  });

  const groupMemberIdToPlayerName = useMemo(() => {
    const map = new Map<string, string>();
    if (groups) {
      groups.forEach((group) => {
        if (group.members) {
          group.members.forEach((member) => {
            if (member.playerProfile) {
              map.set(member.id as string, member.playerProfile.username);
            }
          });
        }
      });
    }
    return map;
  }, [groups]);

  const reservationIdToReservation = useMemo(() => {
    const map = new Map<
      string,
      ResultOf<
        typeof EVENT_RESERVATIONS_CONFIRMED
      >["eventReservations"]["nodes"][0]
    >();
    reservations?.forEach((reservation) => {
      map.set(reservation.id, reservation);
    });
    return map;
  }, [reservations]);

  const exportToPng = async () => {
    if (!contentRef.current) return;

    setExporting(true);
    try {
      const contentElement = contentRef.current;
      const img = contentElement.querySelector("img") as HTMLImageElement;
      const originalSrc = img?.src;

      let restoredSrc: string | null = null;
      if (originalSrc && img) {
        const corsProxies = [
          `https://api.allorigins.win/raw?url=${encodeURIComponent(originalSrc)}`,
          `https://corsproxy.io/?${encodeURIComponent(originalSrc)}`,
          `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(originalSrc)}`,
        ];

        for (const proxyUrl of corsProxies) {
          try {
            const testImg = new Image();
            testImg.crossOrigin = "anonymous";
            await new Promise<void>((resolve, reject) => {
              testImg.onload = () => resolve();
              testImg.onerror = () => reject(new Error("Failed to load"));
              testImg.src = proxyUrl;
            });

            restoredSrc = img.src;
            img.src = proxyUrl;
            img.crossOrigin = "anonymous";

            await new Promise<void>((resolve, reject) => {
              if (img.complete) {
                resolve();
              } else {
                img.onload = () => resolve();
                img.onerror = () => reject(new Error("Failed to load"));
              }
            });
            break;
          } catch {
            continue;
          }
        }
      }

      const FOUR_K_LONGEST_EDGE_PX = 3840;
      const baseWidth = contentElement.scrollWidth;
      const baseHeight = contentElement.scrollHeight;
      const longestEdge = Math.max(baseWidth, baseHeight);
      const exportScale =
        longestEdge > 0 ? FOUR_K_LONGEST_EDGE_PX / longestEdge : 1;

      const dataUrl = await domtoimage.toPng(contentElement, {
        quality: 1.0,
        width: baseWidth,
        height: baseHeight,
        scale: exportScale,
      });

      if (restoredSrc && img) {
        img.src = restoredSrc;
        img.removeAttribute("crossOrigin");
      }

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `seats-map-${eventId}-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      // Export failed silently
    } finally {
      setExporting(false);
    }
  };

  if (seatsLoading || reservationsLoading || groupsLoading) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  return (
    <>
      <Group justify="right" mb="sm">
        <button
          onClick={exportToPng}
          disabled={exporting}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            cursor: "pointer",
            border: "1px solid #373A40",
            borderRadius: 8,
            background: "#25262b",
            color: "#C1C2C5",
          }}
        >
          <IconDownload size={16} />
          <span>{exporting ? "Exporting..." : "Export to PNG"}</span>
        </button>
      </Group>
      <Box style={{ flex: "1", minWidth: 0 }} ref={containerRef}>
        <TransformWrapper>
          <TransformComponent>
            <Box ref={contentRef} data-export-content>
              <img
                src={`${CDN_BASE_URL}/${imageUrl}`}
                alt="Venue Plan"
                style={{
                  width: "100%",
                  display: "block",
                  backgroundColor: "lightgray",
                }}
              />
              {seats &&
                Array.isArray(seats) &&
                seats.map(
                  ({
                    id,
                    name,
                    reservedByReservationId,
                    reservedByGroupMemberId,
                    configuration: {
                      position: { x, y },
                      size: seatSize,
                    },
                    status,
                  }) => {
                    const playerName = reservedByGroupMemberId
                      ? (groupMemberIdToPlayerName.get(
                          reservedByGroupMemberId,
                        ) ?? "N/A")
                      : reservedByReservationId
                        ? (reservationIdToReservation.get(
                            reservedByReservationId,
                          )?.name ?? "N/A")
                        : "N/A";
                    return (
                      <svg
                        key={id}
                        viewBox={"0 0 100 130"}
                        style={{
                          width: `${(seatSize ?? 1) * 2}%`,
                          height: `${(seatSize ?? 1) * 2.6}%`,
                          position: "absolute",
                          top: `${y}%`,
                          left: `${x}%`,
                          transform: "translate(-50%, -50%)",
                          cursor: "pointer",
                          overflow: "visible",
                        }}
                        onClick={() => {
                          if (reservedByReservationId) {
                            modals.open({
                              children: (
                                <SeatModal
                                  reservationId={reservedByReservationId}
                                />
                              ),
                            });
                          }
                        }}
                      >
                        <title>{name}</title>
                        <circle
                          cx={50}
                          cy={50}
                          r={50}
                          fill={
                            status === "SOLD"
                              ? "red"
                              : status === "RESERVED"
                                ? "orange"
                                : "green"
                          }
                          stroke="black"
                          strokeWidth="1"
                        />
                        {status !== "AVAILABLE" && (
                          <foreignObject
                            x="0"
                            y="130"
                            width="100"
                            height="30"
                            style={{ overflow: "visible" }}
                          >
                            <div
                              style={{
                                textAlign: "center",
                                fontSize: "40px",
                                fontWeight: "bold",
                                color: "black",
                                textShadow:
                                  "4px 4px 8px white, -4px -4px 8px white, 4px -4px 8px white, -4px 4px 8px white",
                                pointerEvents: "none",
                                userSelect: "none",
                                fontFamily: "Arial, sans-serif",
                                whiteSpace: "nowrap",
                                overflow: "visible",
                                lineHeight: "1",
                              }}
                            >
                              {playerName || "N/A"}
                            </div>
                          </foreignObject>
                        )}
                      </svg>
                    );
                  },
                )}
            </Box>
          </TransformComponent>
        </TransformWrapper>
      </Box>
    </>
  );
};

const deriveEventStatus = (event: {
  startAt?: string | null;
  endAt?: string | null;
  startRegistrationsAt?: string | null;
  endRegistrationsAt?: string | null;
  visibleAt?: string | null;
}): string => {
  const now = new Date();
  if (event.endAt && new Date(event.endAt as string) <= now) return "Ended";
  if (event.startAt && new Date(event.startAt as string) <= now)
    return "In Progress";
  if (
    event.endRegistrationsAt &&
    new Date(event.endRegistrationsAt as string) <= now
  )
    return "Registrations Closed";
  if (
    event.startRegistrationsAt &&
    new Date(event.startRegistrationsAt as string) <= now
  )
    return "Registrations Open";
  if (event.visibleAt && new Date(event.visibleAt as string) <= now)
    return "Visible";
  return "Draft";
};

export const EventDashboard = () => {
  const { classes } = useStyles();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const eventId = useParams().eventId!;
  const navigate = useNavigate();
  const { loading, data, refetch } = useQuery<any>(EVENT_DETAILS, {
    variables: { id: eventId },
  });

  const { getLimit } = useOrganizationLimits([
    "EVENTS_MAX_TICKETS_TYPES",
    "EVENTS_MAX_TICKETS",
    "EVENTS_MAX_GROUP_SIZE",
  ]);
  const ticketTypesLimit = getLimit("EVENTS_MAX_TICKETS_TYPES");
  const maxTicketsLimit = getLimit("EVENTS_MAX_TICKETS");
  const groupSizeLimit = getLimit("EVENTS_MAX_GROUP_SIZE");

  if (loading) {
    return (
      <div className="wp-fade-in">
        <DetailPageSkeleton />
      </div>
    );
  }

  const totalTicketsCount =
    data?.eventTicketConfigurations.reduce(
      (acc: any, { maxCount }: any) => acc + maxCount,
      0,
    ) ?? 0;
  const totalTicketsReserved =
    data?.eventTicketConfigurations.reduce(
      (acc: any, { currentCount }: any) => acc + currentCount,
      0,
    ) ?? 0;

  const fillPercentage =
    totalTicketsCount > 0
      ? Math.min((totalTicketsReserved / totalTicketsCount) * 100, 100)
      : 0;

  const isVisible =
    data?.event?.visibleAt &&
    new Date(data.event.visibleAt as string) <= new Date();

  const formatDate = (date: string | null | undefined) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleString();
  };

  const eventStatus = data?.event
    ? deriveEventStatus({
        startAt: data.event.startAt as string | null,
        endAt: data.event.endAt as string | null,
        startRegistrationsAt: data.event.startRegistrationsAt as string | null,
        endRegistrationsAt: data.event.endRegistrationsAt as string | null,
        visibleAt: data.event.visibleAt as string | null,
      })
    : "Draft";

  return (
    <div className="wp-fade-in wp-stagger-children">
      <DetailPageHeader
        title={data?.event?.title ?? "Event Dashboard"}
        status={eventStatus}
        onEdit={() => navigate("settings/general")}
        onRefresh={() => refetch()}
        showBreadcrumbs={false}
      />

      {/* Quick Stats */}
      <SimpleGrid cols={{ base: 1, sm: 4, md: 4 }} spacing="lg" mb="lg">
        <StatCard
          label="Total Tickets"
          value={totalTicketsCount}
          iconType="tickets"
          progress={fillPercentage}
          progressLabel={`${totalTicketsReserved} reserved`}
          progressColor={fillPercentage >= 100 ? "green" : "cyan"}
        />
        <StatCard
          label="Tickets Reserved"
          value={totalTicketsReserved}
          iconType="tickets"
          iconColor="purple"
          description={
            maxTicketsLimit
              ? `Max ${formatLimitValue(maxTicketsLimit.max, maxTicketsLimit.type)} per event`
              : undefined
          }
        />
        <StatCard
          label="Visibility"
          value={isVisible ? "Visible" : "Hidden"}
          iconType="event"
          iconColor={isVisible ? "green" : "amber"}
          description={
            data?.event?.visibleAt
              ? `Since ${formatDate(data.event.visibleAt as string)}`
              : "No visibility date set"
          }
        />
        <StatCard
          label="Fill Rate"
          value={`${Math.round(fillPercentage)}%`}
          iconType="stats"
          iconColor={fillPercentage >= 90 ? "green" : "cyan"}
          description={
            [
              ticketTypesLimit
                ? `Max ${formatLimitValue(ticketTypesLimit.max, ticketTypesLimit.type)} ticket types`
                : null,
              groupSizeLimit
                ? `Max group size ${formatLimitValue(groupSizeLimit.max, groupSizeLimit.type)}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ") || undefined
          }
        />
      </SimpleGrid>

      {/* Main Grid */}
      <SimpleGrid cols={2} spacing="lg">
        {/* Event Info Card */}
        <Card className={classes.card} p={0}>
          <Box className={classes.cardHeader}>
            <Group justify="space-between">
              <Group gap="sm">
                <Box className={classes.cardHeaderIcon}>
                  <IconCalendarEvent size={20} />
                </Box>
                <Text className={classes.cardTitle}>{data?.event?.title}</Text>
              </Group>
              <StatusBadge
                status={isVisible ? "Visible" : "Hidden"}
                color={isVisible ? "green" : "rose"}
              />
            </Group>
          </Box>
          <Box className={classes.cardBody}>
            <SimpleGrid cols={2} spacing="md">
              <StatCard
                label="Start Date"
                value={formatDate(data?.event?.startAt as string)}
                icon={<IconCalendar size={16} stroke={1.5} />}
                className=""
              />
              <StatCard
                label="End Date"
                value={formatDate(data?.event?.endAt as string)}
                icon={<IconCalendarEvent size={16} stroke={1.5} />}
                className=""
              />
            </SimpleGrid>
          </Box>
        </Card>

        {/* Registrations Card */}
        <Card className={classes.card} p={0}>
          <Box className={classes.cardHeader}>
            <Group justify="space-between">
              <Group gap="sm">
                <Box className={classes.cardHeaderIcon}>
                  <IconCalendarEvent size={20} />
                </Box>
                <Text className={classes.cardTitle}>Registrations</Text>
              </Group>
              <StatusBadge
                status={
                  data?.event?.startRegistrationsAt &&
                  new Date(data.event.startRegistrationsAt as string) <=
                    new Date() &&
                  (!data?.event?.endRegistrationsAt ||
                    new Date(data.event.endRegistrationsAt as string) >
                      new Date())
                    ? "Open"
                    : data?.event?.endRegistrationsAt &&
                        new Date(data.event.endRegistrationsAt as string) <=
                          new Date()
                      ? "Closed"
                      : data?.event?.startRegistrationsAt
                        ? "Pending"
                        : "Not Configured"
                }
              />
            </Group>
          </Box>
          <Box className={classes.cardBody}>
            <SimpleGrid cols={2} spacing="md">
              <StatCard
                label="Opens At"
                value={formatDate(data?.event?.startRegistrationsAt as string)}
                icon={<IconCalendar size={16} stroke={1.5} />}
                className=""
              />
              <StatCard
                label="Closes At"
                value={formatDate(data?.event?.endRegistrationsAt as string)}
                icon={<IconCalendarEvent size={16} stroke={1.5} />}
                className=""
              />
            </SimpleGrid>
          </Box>
        </Card>

        {/* Tickets Available Card */}
        <Card className={classes.card} p={0}>
          <Box className={classes.cardHeader}>
            <Group gap="sm">
              <Box className={classes.cardHeaderIcon}>
                <IconTicket size={20} />
              </Box>
              <Text className={classes.cardTitle}>Tickets Available</Text>
            </Group>
          </Box>
          <Box className={classes.cardBody}>
            {data?.eventTicketConfigurations.map(
              ({ name, currentCount, maxCount }: any) => {
                const ticketFill =
                  maxCount > 0 ? (currentCount / maxCount) * 100 : 0;
                return (
                  <Box key={name} className={classes.ticketCard}>
                    <Group justify="space-between" mb={4}>
                      <Text className={classes.ticketName}>{name}</Text>
                      <Text className={classes.ticketCount}>
                        <AbbrevitatedNumber value={currentCount} /> /{" "}
                        <AbbrevitatedNumber value={maxCount} />
                      </Text>
                    </Group>
                    <Progress
                      value={ticketFill}
                      color={ticketFill >= 90 ? "green" : "purple"}
                      size="xs"
                      radius="xl"
                    />
                  </Box>
                );
              },
            )}
          </Box>
        </Card>

        {/* Seats Map Card */}
        {data?.event?.venue?.configuration?.imageUrl && (
          <Card className={classes.seatsCard} p={0}>
            <Box className={classes.cardHeader}>
              <Group gap="sm">
                <Box className={classes.cardHeaderIcon}>
                  <IconMap size={20} />
                </Box>
                <Text className={classes.cardTitle}>Seats Map</Text>
              </Group>
            </Box>
            <Box className={classes.cardBody}>
              <SeatsMap
                eventId={eventId}
                imageUrl={data?.event?.venue?.configuration?.imageUrl}
              />
            </Box>
          </Card>
        )}
      </SimpleGrid>
    </div>
  );
};
